// src/pages/Messages.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Messages.css";
import { useGraphAuth } from "../auth/useGraphAuth";

const GET_MESSAGES_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllContactMessages?code=R6i08dadDY85lPMTFo9K5giCD9DKWjVO0ylVQpochg36AzFuzFslYw==";

const GET_USERS_API =
    "https://oppiesfunc.azurewebsites.net/api/getUsers?code=JA76W-z030K296T2l7-RFj85STebU2t-MSjpNIR7iytCAzFubE_a9w==";

const GET_SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllShops?code=Dx_kliFsd4-5j40_pt5i6RsR36dv01K92JHMPb6eQRHBAzFuQKNV3A==";

const GET_STORES_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllStores?code=KBfxDkDBLTTvOxMxkXQFzbNyGeZJGg3PLAGKGbTnPkKvAzFuzCtYxw==";

const MARK_AS_READ_API =
    "https://oppiesfunc.azurewebsites.net/api/markMessageAsRead?code=6yL1smQmFH0jxegpkdEGLi57W7xqFwfrjRY6a50j0wNTAzFuoUJSVg==";

function Messages() {
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [replySubject, setReplySubject] = useState("");
    const [replyMessage, setReplyMessage] = useState("");
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const { getToken, login, account } = useGraphAuth();

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const normalizeEmail = (email) =>
        typeof email === "string" ? email.trim().toLowerCase() : "";

    const normalizeListResponse = (response) => {
        const data = response?.data;

        if (Array.isArray(data)) {
            return data;
        }

        if (Array.isArray(data?.items)) {
            return data.items;
        }

        if (Array.isArray(data?.users)) {
            return data.users;
        }

        if (Array.isArray(data?.shops)) {
            return data.shops;
        }

        if (Array.isArray(data?.stores)) {
            return data.stores;
        }

        if (Array.isArray(data?.messages)) {
            return data.messages;
        }

        return [];
    };

    const findByEmail = (items, email) => {
        const targetEmail = normalizeEmail(email);

        if (!targetEmail) {
            return null;
        }

        return (
            items.find(
                (item) => normalizeEmail(item?.email) === targetEmail
            ) || null
        );
    };

    const formatRole = (role) => {
        const normalizedRole = String(role || "")
            .trim()
            .toLowerCase();

        switch (normalizedRole) {
            case "opshopper":
            case "shopper":
            case "user":
                return "OpShopper";

            case "opshop":
            case "shop":
                return "OpShop";

            case "store":
                return "Store";

            case "admin":
                return "Admin";

            default:
                return role || "Unknown";
        }
    };

    const getUserName = (user) =>
        user?.did ||
        user?.displayName ||
        user?.name ||
        user?.userName ||
        user?.firstName ||
        null;

    const getShopName = (shop) =>
        shop?.name ||
        shop?.shopId ||
        shop?.displayName ||
        null;

    const getStoreName = (store) =>
        store?.name ||
        store?.storeId ||
        store?.displayName ||
        null;

    const getMessageFallbackName = (message) =>
        message?.senderName ||
        message?.name ||
        message?.displayName ||
        message?.firstName ||
        null;

    const enrichMessage = (message, users, shops, stores) => {
        const userMatch = findByEmail(users, message?.senderEmail);
        const shopMatch = findByEmail(shops, message?.senderEmail);
        const storeMatch = findByEmail(stores, message?.senderEmail);

        /*
         * Match order:
         *
         * 1. User
         * 2. Shop
         * 3. Store
         *
         * The matched document's own role is used wherever possible.
         */

        if (userMatch) {
            return {
                ...message,
                senderName:
                    getUserName(userMatch) ||
                    getMessageFallbackName(message) ||
                    "Unknown",
                senderRole: formatRole(
                    userMatch.role || "OpShopper"
                ),
            };
        }

        if (shopMatch) {
            return {
                ...message,
                senderName:
                    getShopName(shopMatch) ||
                    getMessageFallbackName(message) ||
                    "Unknown",
                senderRole: formatRole(
                    shopMatch.role || "OpShop"
                ),
            };
        }

        if (storeMatch) {
            return {
                ...message,
                senderName:
                    getStoreName(storeMatch) ||
                    getMessageFallbackName(message) ||
                    "Unknown",
                senderRole: formatRole(
                    storeMatch.role || "Store"
                ),
            };
        }

        return {
            ...message,
            senderName:
                getMessageFallbackName(message) ||
                message?.senderEmail?.split("@")?.[0] ||
                "Unknown",
            senderRole: formatRole(message?.senderRole || message?.role),
        };
    };

    const fetchAllData = async () => {
        setLoading(true);
        setLoadError("");

        try {
            const [
                messageResponse,
                userResponse,
                shopResponse,
                storeResponse,
            ] = await Promise.all([
                axios.get(GET_MESSAGES_API),
                axios.get(GET_USERS_API),
                axios.get(GET_SHOPS_API),
                axios.get(GET_STORES_API),
            ]);

            const messageItems =
                normalizeListResponse(messageResponse);

            const users =
                normalizeListResponse(userResponse);

            const shops =
                normalizeListResponse(shopResponse);

            const stores =
                normalizeListResponse(storeResponse);

            const enrichedMessages = messageItems.map((message) =>
                enrichMessage(message, users, shops, stores)
            );

            /*
             * Show newest messages first.
             */
            enrichedMessages.sort((a, b) => {
                const aTime = new Date(
                    a.timestamp || a.createdAt || 0
                ).getTime();

                const bTime = new Date(
                    b.timestamp || b.createdAt || 0
                ).getTime();

                return bTime - aTime;
            });

            setMessages(enrichedMessages);
        } catch (err) {
            console.error(
                "Error loading messages, users, shops or stores:",
                err?.response?.data || err?.message || err
            );

            setLoadError(
                "Messages could not be loaded. Check the Messages, Users, Shops and Stores API endpoints."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleView = async (message) => {
        setSelectedMessage(message);
        setReplySubject(`RE: ${message.subject || ""}`);
        setReplyMessage("");
        setShowReplyModal(true);

        if (!message.read) {
            try {
                await axios.post(MARK_AS_READ_API, {
                    id: message.id,
                    senderEmail: message.senderEmail,
                });

                setMessages((previousMessages) =>
                    previousMessages.map((item) =>
                        item.id === message.id
                            ? { ...item, read: true }
                            : item
                    )
                );

                setSelectedMessage((currentMessage) =>
                    currentMessage?.id === message.id
                        ? { ...currentMessage, read: true }
                        : currentMessage
                );
            } catch (err) {
                console.warn(
                    "Failed to mark message as read:",
                    err?.response?.data || err?.message || err
                );
            }
        }
    };

    const handleClose = () => {
        setSelectedMessage(null);
        setReplySubject("");
        setReplyMessage("");
        setShowReplyModal(false);
    };

    const sendReply = async () => {
        if (!selectedMessage || !replyMessage.trim()) {
            return;
        }

        try {
            const accessToken = await getToken();

            if (!accessToken) {
                alert(
                    "Please sign in with Microsoft before sending a reply."
                );
                return;
            }

            const emailPayload = {
                message: {
                    subject:
                        replySubject.trim() ||
                        `RE: ${selectedMessage.subject || ""}`,
                    body: {
                        contentType: "Text",
                        content: replyMessage.trim(),
                    },
                    toRecipients: [
                        {
                            emailAddress: {
                                address: selectedMessage.senderEmail,
                            },
                        },
                    ],
                },
                saveToSentItems: true,
            };

            await axios.post(
                "https://graph.microsoft.com/v1.0/users/support@oppies.nz/sendMail",
                emailPayload,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            alert("Reply sent via Microsoft Graph.");
            handleClose();
        } catch (error) {
            console.error(
                "Graph email error:",
                error?.response?.data ||
                error?.response ||
                error?.message ||
                error
            );

            alert("Failed to send the reply via Microsoft Graph.");
        }
    };

    const formatMessageDate = (message) => {
        const value = message?.timestamp || message?.createdAt;

        if (!value) {
            return "Unknown";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return String(value);
        }

        return date.toLocaleString();
    };

    return (
        <div className="messages-page">
            <h2 className="messages-title">Contact Messages</h2>

            {!account && (
                <button
                    className="login-button"
                    onClick={login}
                >
                    Sign in with Microsoft
                </button>
            )}

            {loadError && (
                <div className="messages-error">
                    {loadError}
                </div>
            )}

            {loading ? (
                <p>Loading messages…</p>
            ) : (
                <table className="messages-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Email</th>
                            <th>Subject</th>
                            <th>Date</th>
                            <th>View</th>
                        </tr>
                    </thead>

                    <tbody>
                        {messages.length === 0 ? (
                            <tr>
                                <td colSpan="7">
                                    No contact messages found.
                                </td>
                            </tr>
                        ) : (
                            messages.map((message) => (
                                <tr
                                    key={message.id}
                                    className={
                                        message.read
                                            ? "read"
                                            : "unread"
                                    }
                                >
                                    <td>
                                        {!message.read && (
                                            <span className="new-dot">
                                                ●
                                            </span>
                                        )}
                                    </td>

                                    <td>{message.senderName}</td>

                                    <td>{message.senderRole}</td>

                                    <td>{message.senderEmail}</td>

                                    <td>{message.subject}</td>

                                    <td>
                                        {formatMessageDate(message)}
                                    </td>

                                    <td>
                                        <button
                                            className="view-button"
                                            onClick={() =>
                                                handleView(message)
                                            }
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}

            {showReplyModal && selectedMessage && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="message-box">
                            <h4>Message from</h4>

                            <p>
                                <strong>Name:</strong>{" "}
                                {selectedMessage.senderName}
                            </p>

                            <p>
                                <strong>Role:</strong>{" "}
                                {selectedMessage.senderRole}
                            </p>

                            <p>
                                <strong>Email:</strong>{" "}
                                {selectedMessage.senderEmail}
                            </p>

                            <p>
                                <strong>Subject:</strong>{" "}
                                {selectedMessage.subject}
                            </p>

                            <p>
                                <strong>Message:</strong>{" "}
                                {selectedMessage.message}
                            </p>
                        </div>

                        <div className="reply-box">
                            <h4>Reply</h4>

                            <label>Subject:</label>

                            <input
                                type="text"
                                value={replySubject}
                                onChange={(event) =>
                                    setReplySubject(
                                        event.target.value
                                    )
                                }
                            />

                            <label>Message:</label>

                            <textarea
                                rows="5"
                                value={replyMessage}
                                onChange={(event) =>
                                    setReplyMessage(
                                        event.target.value
                                    )
                                }
                            />

                            <br />

                            <button
                                className="send-button"
                                onClick={sendReply}
                            >
                                Send Reply
                            </button>

                            <button
                                className="close-button"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Messages;